'use client';

import { useState, useCallback, useEffect } from 'react';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    CardDescription,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Copy, Upload } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { textReplacerContent } from '../../data/text-replacer-content';

export default function TextReplacer() {
    const [inputText, setInputText] = useState('');
    const [outputText, setOutputText] = useState('');
    const [findText, setFindText] = useState('');
    const [replaceWith, setReplaceWith] = useState('');
    const [useRegex, setUseRegex] = useState(false);
    const [caseSensitive, setCaseSensitive] = useState(false);
    const [replaceAll, setReplaceAll] = useState(true);
    const [preview, setPreview] = useState(true);

    const { toast } = useToast();

    const performReplace = useCallback(() => {
        if (!inputText || !findText) {
            setOutputText(inputText);
            return;
        }

        try {
            let result = inputText;

            if (useRegex) {
                const flags = replaceAll
                    ? caseSensitive
                        ? 'g'
                        : 'gi'
                    : caseSensitive
                        ? ''
                        : 'i';

                const regex = new RegExp(findText, flags);
                result = inputText.replace(regex, replaceWith);
            } else {
                if (replaceAll) {
                    const escaped = findText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                    const flags = caseSensitive ? 'g' : 'gi';
                    const regex = new RegExp(escaped, flags);
                    result = inputText.replace(regex, replaceWith);
                } else {
                    const index = caseSensitive
                        ? inputText.indexOf(findText)
                        : inputText
                            .toLowerCase()
                            .indexOf(findText.toLowerCase());

                    if (index !== -1) {
                        result =
                            inputText.substring(0, index) +
                            replaceWith +
                            inputText.substring(index + findText.length);
                    }
                }
            }

            setOutputText(result);
        } catch (error) {
            setOutputText('Invalid regular expression');
        }
    }, [
        inputText,
        findText,
        replaceWith,
        useRegex,
        caseSensitive,
        replaceAll,
    ]);

    useEffect(() => {
        if (preview) performReplace();
    }, [performReplace, preview]);

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            const content = event.target?.result as string;
            setInputText(content);
        };
        reader.readAsText(file);
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        toast({
            title: 'Copied!',
            description: 'Text copied to clipboard.',
        });
    };

    return (
        <div className="container mx-auto px-6 lg:px-8 py-10">

            {/* HEADER */}
            <div className="mb-10">
                <ToolPageIntro title="Text Replacer">
                Replace text strings instantly with our free online tool. Find and replace text in documents, code, and any text content with advanced options.
            </ToolPageIntro>
            </div>

            {/* FIND & REPLACE SETTINGS */}
            <Card>
                <CardHeader>
                    <CardTitle>Find & Replace</CardTitle>
                    <CardDescription>
                        Configure search and replacement options
                    </CardDescription>
                </CardHeader>

                <CardContent className="space-y-6">

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <Label className="mb-2 block">Find</Label>
                            <input
                                type="text"
                                value={findText}
                                onChange={(e) => setFindText(e.target.value)}
                                className="w-full border rounded-md px-3 py-2"
                            />
                        </div>

                        <div>
                            <Label className="mb-2 block">Replace With</Label>
                            <input
                                type="text"
                                value={replaceWith}
                                onChange={(e) => setReplaceWith(e.target.value)}
                                className="w-full border rounded-md px-3 py-2"
                            />
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-6 pt-2">
                        <div className="flex items-center gap-2">
                            <Switch checked={useRegex} onCheckedChange={setUseRegex} />
                            <span>Use Regex</span>
                        </div>

                        <div className="flex items-center gap-2">
                            <Switch checked={caseSensitive} onCheckedChange={setCaseSensitive} />
                            <span>Case Sensitive</span>
                        </div>

                        <div className="flex items-center gap-2">
                            <Switch checked={replaceAll} onCheckedChange={setReplaceAll} />
                            <span>Replace All</span>
                        </div>

                        <div className="flex items-center gap-2">
                            <Switch checked={preview} onCheckedChange={setPreview} />
                            <span>Live Preview</span>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* INPUT / OUTPUT */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-10">

                <div>
                    <div className="flex justify-between items-center mb-2">
                        <Label>Input</Label>
                        <div className="flex gap-2">
                            <label className="cursor-pointer">
                                <Upload className="w-4 h-4" />
                                <input
                                    type="file"
                                    hidden
                                    onChange={handleFileUpload}
                                />
                            </label>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => copyToClipboard(inputText)}
                            >
                                <Copy className="w-4 h-4 mr-1" /> Copy
                            </Button>
                        </div>
                    </div>

                    <Textarea
                        value={inputText}
                        onChange={(e) => setInputText(e.target.value)}
                        className="min-h-[350px]"
                    />
                </div>

                <div>
                    <div className="flex justify-between items-center mb-2">
                        <Label>Result</Label>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => copyToClipboard(outputText)}
                        >
                            <Copy className="w-4 h-4 mr-1" /> Copy
                        </Button>
                    </div>

                    <Textarea
                        value={outputText}
                        readOnly
                        className="min-h-[350px] bg-muted/50"
                    />
                </div>
            </div>

            {/* SEO / INFORMATIONAL CONTENT */}
            <div className="mt-16">
                <InformationalContent
                    title={textReplacerContent.title}
                    description={textReplacerContent.description}
                    content={textReplacerContent.content}
                    faqs={textReplacerContent.faqs}
                    relatedTools={textReplacerContent.relatedTools}
                />
            </div>

        </div>
    );
}
